import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShoppingCart,
  Truck,
  HeartPulse,
  Store,
  ArrowRight,
  Sparkles,
  ChevronDown,
} from "lucide-react";

const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  border: "#E6DFDC",
  text: "#FFF8F6",
  textMuted: "#D8B4B8",
  panelBg: "#FFFFFF",
  cardBg: "#F6EEEC",
};

const solutions = [
  {
    to: "/solutions/ecommerce",
    icon: ShoppingCart,
    title: "E-Commerce",
    desc: "Online payments, seller onboarding & customer verification.",
  },
  {
    to: "/solutions/logistics",
    icon: Truck,
    title: "Logistics",
    desc: "Fleet payments, COD settlement & driver verification.",
  },
  {
    to: "/solutions/healthcare",
    icon: HeartPulse,
    title: "Healthcare",
    desc: "Patient verification, claims & healthcare payments.",
  },
  {
    to: "/solutions/marketplace",
    icon: Store,
    title: "Marketplace",
    desc: "Vendor onboarding, KYC & commission management.",
  },
];

export default function SolutionsMenu() {
  const [solutionsOpen, setSolutionsOpen] = useState(false);

  return (
    <li
      className="relative"
      onMouseEnter={() => setSolutionsOpen(true)}
      onMouseLeave={() => setSolutionsOpen(false)}
    >
      <span
        className="flex items-center gap-1.5 py-1 transition-colors"
        style={{ color: solutionsOpen ? theme.text : theme.textMuted }}
      >
        <Link to="/solutions" data-cursor="hover" className="hover:text-[#FFF8F6]">
          Solutions
        </Link>
        <ChevronDown size={14} className={`transition-transform duration-300 ${solutionsOpen ? "rotate-180" : ""}`} />
      </span>

      <AnimatePresence>
        {solutionsOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed left-6 top-20 w-[800px] max-w-[calc(100vw-2rem)] rounded-3xl shadow-2xl z-50 overflow-hidden"
            style={{ backgroundColor: theme.panelBg, border: `1px solid ${theme.border}` }}
          >
            <div className="relative grid grid-cols-3 gap-8 p-10">
              <div className="col-span-2 grid grid-cols-2 gap-6">
                {solutions.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-cursor="hover"
                      className="group p-5 rounded-2xl border transition-colors duration-300 hover:bg-[#F6EEEC]"
                      style={{ borderColor: theme.border }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = `${theme.accent}80`)}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = theme.border)}
                    >
                      <div
                        className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors"
                        style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                      >
                        <Icon size={20} />
                      </div>

                      <h4 className="font-semibold transition-colors" style={{ color: theme.ink }}>
                        <span className="group-hover:text-[color:var(--accent-hover)]" style={{ "--accent-hover": theme.accent }}>
                          {item.title}
                        </span>
                      </h4>

                      <p className="text-sm mt-2 leading-relaxed" style={{ color: theme.inkMuted }}>
                        {item.desc}
                      </p>
                    </Link>
                  );
                })}
              </div>

              <div
                className="rounded-2xl p-6 flex flex-col justify-between"
                style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}
              >
                <div>
                  <p
                    className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-xs"
                    style={{ color: theme.accent }}
                  >
                    <Sparkles size={13} />
                    Featured Solution
                  </p>

                  <h3 className="text-2xl font-bold mt-3" style={{ color: theme.ink }}>Marketplace Suite</h3>

                  <p className="mt-3 text-sm leading-relaxed" style={{ color: theme.inkMuted }}>
                    End-to-end onboarding, payments and verification built for multi-vendor platforms.
                  </p>
                </div>

                <Link
                  to="/solutions"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="mt-6 inline-flex justify-center items-center gap-2 px-6 py-3 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  Explore Solutions
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}